import { useState } from 'react';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { createSha256 } from '@ibk/shared-utils';
import { ApiErrors } from '../../enums';
import { usePostPixAgencyAccountMutation, usePostPixAgencyAccountV2Mutation } from '../../services/pixApi';
import { handlePixError } from '../../utils/pixErrorHandler';
const ERRORS_MESSAGES = {
    [ApiErrors.CAN_NOT_TRANSFER_PIX]: 'transaction-not-done-try-again-later',
    [ApiErrors.NO_FUNDS_AVAILABLE]: 'insufficient-balance-modal-title',
    [ApiErrors.ERROR_NO_FUNDS_AVAILABLE]: 'insufficient-balance-modal-title',
    [ApiErrors.PIN_FAILURE_TO_VALIDATE]: 'pin-failure-to-validate-error',
    [ApiErrors.TIMEOUT_ERROR]: 'pix-timeout-error',
    [ApiErrors.PIX_LIMIT_EXCEEDED]: 'pix-limit-exceeded',
    [ApiErrors.PAYMENT_ALREADY_EXISTS]: 'payment-in-processing',
};
function buildV2Body(body) {
    return {
        credential: body.credential,
        amount: body.amount,
        currency: body.currency,
        description: body.description,
        source: 'FINTECH:IBK',
        confirmDuplicate: body.confirmDuplicate,
        accountPayablesId: body.accountPayablesId,
        pixDetails: {
            keyType: 'AGENCY',
            key: body.counterPartyBankAccount ?? '',
            counterpartyName: body.counterPartyName,
            counterpartyDocumentType: body.counterPartyDocumentType,
            counterpartyDocument: body.counterPartyDocument,
            counterpartyBankAccount: body.counterPartyBankAccount,
            counterpartyBankBranch: body.counterPartyBankBranch,
            counterpartyAccountType: body.counterPartyAccountType,
            counterpartyBankCode: body.counterPartyBankCode,
        },
    };
}
export function usePostPixAgencyAccount() {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed, cache } = useFeatureFlag(undefined, {
        autoLoad: false,
    });
    const [triggerV1] = usePostPixAgencyAccountMutation();
    const [triggerV2] = usePostPixAgencyAccountV2Mutation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const [pix, setPix] = useState();
    async function postPixAgencyAccount(body, startAt, rethrow) {
        setLoading(true);
        setError(undefined);
        setPix(undefined);
        try {
            const usePermissions = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAYMENTS_OPERATOR_REQUEST_PAYMENT, undefined, cache?.data);
            const isBaasV2Enabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2, undefined, cache?.data);
            const transactionIdempotencyId = await createSha256({
                transactionAt: startAt,
                accountId: getSelectedAccountInfo()?.accountId,
                amount: body.amount,
                account: body.counterPartyBankAccount?.replace(/\D/g, ''),
                branch: body.counterPartyBankBranch,
                code: body.counterPartyBankCode,
                pin: body.credential,
                accountPayablesId: body.accountPayablesId,
            });
            let result;
            if (isBaasV2Enabled) {
                const v2Body = buildV2Body(body);
                const response = await triggerV2({
                    body: v2Body,
                    transactionIdempotencyId,
                    usePermissions,
                }).unwrap();
                result = response.result;
            }
            else {
                const response = await triggerV1({
                    body,
                    transactionIdempotencyId,
                    usePermissions,
                }).unwrap();
                result = response.result;
            }
            setPix(result);
            return result;
        }
        catch (err) {
            const message = handlePixError({
                error: err,
                translate: t,
                errorMessages: ERRORS_MESSAGES,
                defaultErrorKey: ApiErrors.CAN_NOT_TRANSFER_PIX,
            });
            setError(message);
            if (rethrow) {
                throw new Error(message);
            }
        }
        finally {
            setLoading(false);
        }
    }
    return {
        loading,
        setLoading,
        error,
        setError,
        pix,
        setPix,
        postPixAgencyAccount,
    };
}
